import { Link } from "react-router-dom";
import { Mail, MapPin, ExternalLink } from "lucide-react";
import Logo from "./Logo";

const footerLinks = {
  company: [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about" },
    { label: "Services", href: "/services" },
    { label: "Contact", href: "/contact" },
  ],
  platforms: [
    { label: "LevelUp", href: "https://catalog.skillupwithlevelup.com", external: true },
    { label: "EventsUp", href: "/platforms#eventsup" },
    { label: "StepUp", href: "https://stepup.technofocus.ai/", external: true },
    { label: "All Platforms", href: "/platforms" },
  ],
  legal: [
    { label: "Trust Center", href: "/trust" },
    { label: "Data Processing Addendum", href: "/dpa" },
    { label: "Sub-Processors", href: "/subprocessors" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Cookie Policy", href: "/cookie-policy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "EventsUp Privacy", href: "/eventsup-privacy" },
    { label: "EventsUp Cookies", href: "/eventsup-cookie-policy" },
  ],
};

const offices = [
  { city: "Singapore (HQ)", address: "138 Robinson Road, #26-01 Oxley Tower, Singapore 068906" },
  { city: "New Delhi, India", address: "K-1, Green Park, New Delhi-110016" },
];

export default function Footer() {
  return (
    <footer data-testid="footer" style={{ backgroundColor: '#091F2C' }} className="text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-8">
        {/* Top row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 pb-12 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Logo light={true} />
            <p className="mt-4 text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(185,220,210,0.7)' }}>
              Microsoft Learning Partner specializing in expert-led training across the full Microsoft Cloud ecosystem.
            </p>
            <div className="mt-5 flex items-center gap-2 text-sm" style={{ color: 'rgba(185,220,210,0.8)' }}>
              <Mail size={14} />
              <a href="mailto:contact@technofocus.ai" className="hover:text-white transition-colors">contact@technofocus.ai</a>
            </div>
            <div className="mt-4">
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs border" style={{ borderColor: 'rgba(212,236,142,0.4)', backgroundColor: 'rgba(212,236,142,0.1)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#D4EC8E' }}></span>
                <span style={{ color: '#D4EC8E' }}>Microsoft Learning Partner</span>
              </span>
            </div>
          </div>

          {/* Links */}
          {[
            { title: "Company", links: footerLinks.company },
            { title: "Platforms", links: footerLinks.platforms },
            { title: "Legal", links: footerLinks.legal },
          ].map((group) => (
            <div key={group.title}>
              <h4 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#D4EC8E' }}>{group.title}</h4>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white text-sm transition-colors flex items-center gap-1"
                        style={{ color: 'rgba(185,220,210,0.7)' }}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {link.label} <ExternalLink size={11} />
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="hover:text-white text-sm transition-colors"
                        style={{ color: 'rgba(185,220,210,0.7)' }}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Offices */}
        <div className="py-8 border-b border-white/10">
          <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(212,236,142,0.7)' }}>Global Offices</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {offices.map((office) => (
              <div key={office.city} className="flex items-start gap-2">
                <MapPin size={13} className="shrink-0 mt-0.5" style={{ color: '#D4EC8E' }} />
                <div>
                  <p className="text-white text-sm font-medium">{office.city}</p>
                  <p className="text-xs leading-relaxed mt-0.5" style={{ color: 'rgba(185,220,210,0.6)' }}>{office.address}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ color: 'rgba(185,220,210,0.5)' }}>
          <p>&copy; {new Date().getFullYear()} Technofocus. All rights reserved.</p>
          <Link
            to="/request"
            data-testid="footer-link-ms-service"
            className="hover:text-white transition-colors"
            style={{ color: 'rgba(185,220,210,0.4)' }}
          >
            MS Service
          </Link>
        </div>
      </div>
    </footer>
  );
}
